import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Code, Group, Stack, Text } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { ConfigWarning, WarningSeverity } from "@models/mappingConfig";
import { acknowledgedCodes, canApprove, SEVERITY_ORDER, warningId } from "@utils/mappingConfig";

const SEVERITY_COLOR: Record<WarningSeverity, string> = { blocking: "red", warning: "orange", info: "blue" };

export interface WarningsPanelProps {
  warnings: ConfigWarning[];
  /** Renders Approve, enabled only with no blocking warning and every warning acknowledged. */
  onApprove?: (acknowledgedWarnings: string[]) => void;
  approveLoading?: boolean;
  /** Extra reason Approve is off (e.g. the version is already active). */
  approveDisabled?: boolean;
}

/**
 * Validator findings of a draft, grouped by severity. `blocking` stops Approve,
 * each `warning` must be ticked first, `info` is shown only. Text comes from
 * `mappingConfig.warnings.<code>` and falls back to the server's English message.
 */
export const WarningsPanel: React.FC<WarningsPanelProps> = ({ warnings, onApprove, approveLoading, approveDisabled }) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());

  // New findings (a reload, a 422 with fresh warnings) need a fresh acknowledgement.
  // Keyed on content, not identity, so a parent re-render never clears the ticks.
  const signature = JSON.stringify(warnings.map((w) => [w.severity, w.code, w.path]));
  useEffect(() => setAcknowledged(new Set()), [signature]);

  const indexed = useMemo(() => warnings.map((warning, index) => ({ warning, id: warningId(warning, index) })), [warnings]);
  const approvable = canApprove(warnings, acknowledged);
  const blocking = warnings.filter((w) => w.severity === "blocking").length;

  const toggle = (id: string, checked: boolean) =>
    setAcknowledged((previous) => {
      const next = new Set(previous);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Stack gap="sm" data-testid="warnings-panel">
      {warnings.length === 0 && <Text size="sm" c="dimmed">{t("mappingConfig.warningsPanel.none")}</Text>}
      {SEVERITY_ORDER.map((severity) => {
        const items = indexed.filter(({ warning }) => warning.severity === severity);
        if (items.length === 0) return null;
        return (
          <Alert key={severity} color={SEVERITY_COLOR[severity]} title={t(`mappingConfig.severity.${severity}`, { count: items.length })}
                 data-severity={severity}>
            <Stack gap="xs">
              {items.map(({ warning, id }) => {
                const text = t(`mappingConfig.warnings.${warning.code}`, { ...warning.params, defaultValue: warning.message });
                const body = (
                  <Stack gap={0}>
                    <Text size="sm">{text}</Text>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed" ff="monospace">{warning.code}</Text>
                      {warning.path && <Code fz="xs">{warning.path}</Code>}
                    </Group>
                  </Stack>
                );
                return severity === "warning" && onApprove ? (
                  <Checkbox
                    key={id}
                    checked={acknowledged.has(id)}
                    onChange={(e) => toggle(id, e.currentTarget.checked)}
                    label={body}
                    aria-label={t("mappingConfig.warningsPanel.acknowledge", { code: warning.code })}
                  />
                ) : (
                  <div key={id}>{body}</div>
                );
              })}
            </Stack>
          </Alert>
        );
      })}
      {onApprove && (
        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            {blocking > 0
              ? t("mappingConfig.review.blockedHint", { count: blocking })
              : approvable
                ? t("mappingConfig.review.readyHint")
                : t("mappingConfig.review.ackHint")}
          </Text>
          <Button
            leftSection={<IconCheck size={16} />}
            disabled={!approvable || approveDisabled}
            loading={approveLoading}
            onClick={() => onApprove(acknowledgedCodes(warnings, acknowledged))}
          >
            {t("mappingConfig.review.approve")}
          </Button>
        </Group>
      )}
    </Stack>
  );
};
